import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import api from "../../services/api";
import { FaSearch, FaEye, FaEyeSlash, FaDownload, FaLink, FaClipboardList, FaCalendarAlt, FaPlus, FaChartBar, FaTimes, FaCheckCircle, FaExclamationTriangle, FaEnvelope, FaFileAlt, FaUsers, FaUserCheck, FaUserTimes, FaInfoCircle, FaSignInAlt, FaSignOutAlt } from "react-icons/fa";
import "./admin.css";
import { formatCNIC, isValidCNIC } from "../../utils/formatCNIC";
import { useNavigate, useSearchParams } from "react-router-dom";

const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const getWorkingMinutes = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 0;
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  return Math.floor((end - start) / (1000 * 60));
};

const getExpectedDailyMinutes = (expectedArrival, expectedLeaving) => {
  if (!expectedArrival || !expectedLeaving) return 0;
  const [arrH, arrM] = expectedArrival.split(":").map(Number);
  const [levH, levM] = expectedLeaving.split(":").map(Number);
  return levH * 60 + levM - (arrH * 60 + arrM);
};

const formatHoursMins = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours}h ${mins}m`;
};

const formatDuration = (totalMinutes) => {
  if (totalMinutes === null || totalMinutes === undefined) return "";
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

const getArrivalLateMinutes = (checkInStr, expectedArrivalStr) => {
  if (!checkInStr || !expectedArrivalStr) return 0;
  const checkIn = new Date(checkInStr);
  const expected = new Date(checkInStr);
  const [hours, minutes] = expectedArrivalStr.split(":").map(Number);
  expected.setHours(hours, minutes, 0, 0);
  const diffMs = checkIn - expected;
  return diffMs > 0 ? Math.floor(diffMs / 60000) : 0;
};

const getEarlyDepartureMinutes = (checkOutStr, expectedLeavingStr) => {
  if (!checkOutStr || !expectedLeavingStr) return 0;
  const checkOut = new Date(checkOutStr);
  const expected = new Date(checkOutStr);
  const [hours, minutes] = expectedLeavingStr.split(":").map(Number);
  expected.setHours(hours, minutes, 0, 0);
  const diffMs = expected - checkOut;
  return diffMs > 0 ? Math.floor(diffMs / 60000) : 0;
};

const getExtraTimeMinutes = (checkOutStr, expectedLeavingStr) => {
  if (!checkOutStr || !expectedLeavingStr) return 0;
  const checkOut = new Date(checkOutStr);
  const expected = new Date(checkOutStr);
  const [hours, minutes] = expectedLeavingStr.split(":").map(Number);
  expected.setHours(hours, minutes, 0, 0);
  const diffMs = checkOut - expected;
  return diffMs > 0 ? Math.floor(diffMs / 60000) : 0;
};

const isOffDay = (dayOfWeek, role) => {
  const normalizedRole = String(role || "").toLowerCase();
  if (dayOfWeek === 0) return true;
  if (
    dayOfWeek === 6 &&
    (normalizedRole === "intern" || normalizedRole === "student")
  )
    return true;
  return false;
};

const validateTimeOrder = (arrival, leaving) => {
  if (!arrival || !leaving) return null;
  if (arrival >= leaving) return "Leaving time must be after arrival time";
  return null;
};

const formatTimeString = (timeStr) => {
  if (!timeStr) return null;
  const parts = String(timeStr).split(":");
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const parseDateSafe = (dateStr) => {
  if (!dateStr) return null;
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, y, m, d] = match;
  return {
    year: parseInt(y, 10),
    month: parseInt(m, 10),
    day: parseInt(d, 10),
    toDateObj: () => new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10)),
    dateStr: `${y}-${m}-${d}`,
  };
};

// ==================== ✅ SPECIAL INTERN RULE HELPERS ====================
const INTERN_SPECIAL_WINDOW_START = 12 * 60;
const INTERN_SPECIAL_WINDOW_END = 17 * 60;
const INTERN_SPECIAL_DAILY_EXPECTED_MINUTES = 3 * 60;
const INTERN_SPECIAL_WEEKLY_MIN_MINUTES = 15 * 60;

const isSpecialOffDay = (dayOfWeek) => {
  return dayOfWeek === 0;
};

const getSpecialWindowMinutes = (checkInStr, checkOutStr) => {
  if (!checkInStr || !checkOutStr) return 0;
  const checkIn = new Date(checkInStr);
  const checkOut = new Date(checkOutStr);
  const checkInMins = checkIn.getHours() * 60 + checkIn.getMinutes();
  const checkOutMins = checkOut.getHours() * 60 + checkOut.getMinutes();
  const startMins = Math.max(checkInMins, INTERN_SPECIAL_WINDOW_START);
  const endMins = Math.min(checkOutMins, INTERN_SPECIAL_WINDOW_END);
  return Math.max(0, endMins - startMins);
};

const BASE_URL = "https://employee-tracker-system.desired-techs.com/backend";

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const EmployeeManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [todayStatus, setTodayStatus] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [actionLoading, setActionLoading] = useState({});

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [statsEmployee, setStatsEmployee] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [absentDates, setAbsentDates] = useState([]);
  const [leaveDatesList, setLeaveDatesList] = useState([]);

  const [statsMonthFilter, setStatsMonthFilter] = useState("");
  const [statsYearFilter, setStatsYearFilter] = useState("");

  const [applyExtraTime, setApplyExtraTime] = useState(false);

  const [addTimeError, setAddTimeError] = useState("");
  const [editTimeError, setEditTimeError] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [csvDownloading, setCsvDownloading] = useState(false);

  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [holidayForm, setHolidayForm] = useState({
    holiday_date: "",
    holiday_name: "",
    description: ""
  });

  // ✅ NEW: Holiday month/year filter states
  const now = new Date();
  const [holidayMonthFilter, setHolidayMonthFilter] = useState(now.getMonth() + 1);
  const [holidayYearFilter, setHolidayYearFilter] = useState(now.getFullYear());

  const [showLeaveRequestsModal, setShowLeaveRequestsModal] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveRequestsLoading, setLeaveRequestsLoading] = useState(false);
  const [leaveFilterTab, setLeaveFilterTab] = useState('pending');

  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    cnic: "",
    employmentType: "",
    employeeField: "",
    role: "employee",
    password: "",
    expectedArrival: "",
    expectedLeaving: "",
  });

  const employeeFields = [
    "Web Development", "Flutter Development", "Mobile App Development",
    "UI/UX Design", "Graphic Design", "Backend Development",
    "Frontend Development", "Full Stack Development", "DevOps Engineering",
    "Data Science", "AI/ML Engineering",
    "Cloud Computing", "Cybersecurity", "Blockchain Development",
    "Game Development", "Quality Assurance (QA)", "Project Management",
    "Business Analysis", "Digital Marketing", "SEO Specialist",
    "Content Writing", "Video Editing", "Other",
  ];

  const getToken = () => localStorage.getItem("token");

  const roleFilter = searchParams.get('role') || 'all';

  // ==================== Share Link Functions ====================
  const handleGenerateShareLink = async () => {
    try {
      setGeneratingLink(true);
      setErrorMsg("");
      const token = getToken();
      const response = await api.post(
        "/admin/generate_share_link",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 201 || response.status === 200) {
        const generatedToken = response.data.token;
        const link = `${window.location.origin}/public/stats/${generatedToken}`;
        setShareLink(link);
        setShowShareLinkModal(true);
      }
    } catch (error) {
      setErrorMsg(
        error.response?.data?.message || "Failed to generate share link"
      );
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setSuccessMsg("Link copied to clipboard!");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      const textarea = document.createElement("textarea");
      textarea.value = shareLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setSuccessMsg("Link copied to clipboard!");
      setTimeout(() => setSuccessMsg(""), 3000);
    }
  };

  // ==================== Leave Requests Functions ====================
  const handleGetLeaveRequests = async (status = 'pending') => {
    try {
      setLeaveRequestsLoading(true);
      const token = getToken();
      const response = await api.get(`/admin/leave_requests?status=${status}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setLeaveRequests(response.data.leaves || []);
      }
    } catch (error) {
      console.error("Failed to fetch leave requests:", error);
    } finally {
      setLeaveRequestsLoading(false);
    }
  };

  const handleLeaveAction = async (leaveId, action) => {
    const actionText = action === 'approve' ? 'approve' : 'reject';
    if (!window.confirm(`Are you sure you want to ${actionText} this leave request?`)) return;

    try {
      const token = getToken();
      const response = await api.put("/admin/leave_action", {
        leave_id: leaveId,
        action: action
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        setSuccessMsg(`Leave request ${actionText}d successfully!`);
        setTimeout(() => setSuccessMsg(""), 3000);
        handleGetLeaveRequests(leaveFilterTab);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || `Failed to ${actionText} leave request`);
      setTimeout(() => setErrorMsg(""), 4000);
    }
  };

  // ==================== CSV Download Functions ====================
  const handleDownloadAllMembersCSV = async () => {
    if (!window.confirm(`Download CSV report for ${filteredEmployees.length} members?`)) return;

    try {
      setCsvDownloading(true);
      const token = getToken();
      const currentDate = new Date();
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();

      const allStatsData = [];

      for (const emp of filteredEmployees) {
        try {
          const firstDay = `${year}-${String(month).padStart(2, "0")}-01`;
          const totalDays = new Date(year, month, 0).getDate();
          const lastDay = `${year}-${String(month).padStart(2, "0")}-${totalDays}`;

          const response = await api.get("/admin/filter_attendance", {
            params: {
              employee_id: emp.id,
              start_date: firstDay,
              end_date: lastDay,
            },
            headers: { Authorization: `Bearer ${token}` },
          });

          const attendanceRecords = response.data.attendances || [];

          const leavesResponse = await api.get(`/admin/leave_requests?status=approved`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const employeeLeaves = (leavesResponse.data.leaves || []).filter(l => l.employee_id == emp.id);

          const stats = calculateStatsForEmployee(emp, attendanceRecords, employeeLeaves, year, month - 1);
          allStatsData.push({
            employee: emp,
            stats: stats,
            month: monthNames[month - 1],
            year: year
          });
        } catch (error) {
          console.error(`Failed to fetch stats for ${emp.employeeName}:`, error);
        }
      }

      if (allStatsData.length === 0) {
        setErrorMsg("No data available to download");
        setTimeout(() => setErrorMsg(""), 3000);
        return;
      }

      downloadCSV(allStatsData, `all_members_stats_${month}_${year}`);
      setSuccessMsg(`CSV downloaded for ${allStatsData.length} members!`);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (error) {
      console.error("CSV download error:", error);
      setErrorMsg("Failed to download CSV");
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setCsvDownloading(false);
    }
  };

  const handleDownloadSingleMemberCSV = () => {
    if (!statsEmployee || !statsData) {
      setErrorMsg("No stats data available");
      return;
    }

    try {
      const [year, month] = statsMonthFilter.split("-").map(Number);
      const data = [{
        employee: {
          id: statsEmployee.id,
          employeeName: statsEmployee.employeeName,
          email: statsEmployee.email,
          role: statsEmployee.role,
          employee_fields: statsEmployee.employee_fields,
          employment_type: statsEmployee.employment_type,
        },
        stats: {
          totalWorkingDaysInMonth: statsData.totalWorkingDaysInMonth,
          totalExpectedMonthMinutes: statsData.totalExpectedMonthMinutes,
          totalLeaves: statsData.totalLeaves,
          totalWorkedMinutes: statsData.totalWorkedMinutes,
          workingDaysPassed: statsData.workingDaysPassed,
          currentExpectedMinutes: statsData.currentExpectedMinutes,
          expectedDailyMinutes: statsData.expectedDailyMinutes,
          totalPauseMinutesMonth: statsData.totalPauseMinutesMonth,
          totalLateMinutes: statsData.totalLateMinutes,
          totalEarlyDepartureMinutes: statsData.totalEarlyDepartureMinutes,
          totalExtraTimeMinutes: statsData.totalExtraTimeMinutes,
          totalHolidaysInMonth: statsData.totalHolidaysInMonth,
          totalLeaveDaysInMonth: statsData.totalLeaveDaysInMonth,
          leaveDaysPassed: statsData.leaveDaysPassed,
        },
        month: monthNames[month - 1],
        year: year,
      }];

      downloadCSV(data, `${statsEmployee.employeeName.replace(/\s/g, '_')}_stats_${month}_${year}`);
      setSuccessMsg(`CSV downloaded for ${statsEmployee.employeeName}!`);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (error) {
      console.error("CSV download error:", error);
      setErrorMsg("Failed to download CSV");
      setTimeout(() => setErrorMsg(""), 4000);
    }
  };

  const calculateStatsForEmployee = (employee, attendanceRecords, employeeLeaves, statsYear, statsMonth) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const totalDaysInMonth = new Date(statsYear, statsMonth + 1, 0).getDate();
    const isCurrentMonth = statsMonth === currentMonth && statsYear === currentYear;
    const currentDay = isCurrentMonth ? now.getDate() : totalDaysInMonth;
    const employeeRole = employee.role || "employee";

    const holidayDates = new Set(holidays.map(h => h.holiday_date));
    const leaveDates = new Set(employeeLeaves.map(l => l.leave_date));

    let workingDaysPassed = 0;
    let holidayDaysPassed = 0;
    let leaveDaysPassed = 0;
    const allWorkingDaysPassed = [];

    for (let d = 1; d <= currentDay; d++) {
      const dateObj = new Date(statsYear, statsMonth, d);
      const dayOfWeek = dateObj.getDay();
      const dateStr = `${statsYear}-${String(statsMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

      if (!isOffDay(dayOfWeek, employeeRole)) {
        if (holidayDates.has(dateStr)) {
          holidayDaysPassed++;
        } else if (leaveDates.has(dateStr)) {
          leaveDaysPassed++;
        } else {
          workingDaysPassed++;
          allWorkingDaysPassed.push({
            day: d,
            date: dateStr,
            dayName: dateObj.toLocaleDateString("en-US", { weekday: "short" }),
          });
        }
      }
    }

    let totalWorkingDaysInMonth = 0;
    let totalHolidaysInMonth = 0;
    let totalLeaveDaysInMonth = 0;

    for (let d = 1; d <= totalDaysInMonth; d++) {
      const dayOfWeek = new Date(statsYear, statsMonth, d).getDay();
      const dateStr = `${statsYear}-${String(statsMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

      if (!isOffDay(dayOfWeek, employeeRole)) {
        if (holidayDates.has(dateStr)) {
          totalHolidaysInMonth++;
        } else if (leaveDates.has(dateStr)) {
          totalLeaveDaysInMonth++;
        } else {
          totalWorkingDaysInMonth++;
        }
      }
    }

    const workingDaysSet = new Set(
      attendanceRecords
        .map((r) => r.check_in ? new Date(r.check_in).toISOString().split("T")[0] : null)
        .filter(Boolean),
    );

    const absentDaysList = allWorkingDaysPassed.filter(
      (day) => !workingDaysSet.has(day.date) && !holidayDates.has(day.date) && !leaveDates.has(day.date),
    );

    const totalAbsentDays = absentDaysList.length;

    let expectedDailyMinutes = 0;
    const firstWithExpected = attendanceRecords.find(r => r.expected_arrival && r.expected_leaving);
    if (firstWithExpected) {
      expectedDailyMinutes = getExpectedDailyMinutes(firstWithExpected.expected_arrival, firstWithExpected.expected_leaving);
    } else if (employee.expected_arrival && employee.expected_leaving) {
      expectedDailyMinutes = getExpectedDailyMinutes(employee.expected_arrival, employee.expected_leaving);
    }

    const totalWorkingDaysForExpected = totalWorkingDaysInMonth + (totalLeaveDaysInMonth || 0);
    const totalExpectedMonthMinutes = totalWorkingDaysForExpected * expectedDailyMinutes;
    const actualDaysWorked = workingDaysPassed - totalAbsentDays;
    const currentExpectedMinutes = actualDaysWorked * expectedDailyMinutes;

    const totalWorkedMinutes = attendanceRecords.reduce((total, r) => {
      const grossMinutes = getWorkingMinutes(r.check_in, r.check_out);
      const pauseMinutes = r.total_pause_minutes || 0;
      return total + Math.max(0, grossMinutes - pauseMinutes);
    }, 0);

    const totalPauseMinutesMonth = attendanceRecords.reduce((total, r) => total + (r.total_pause_minutes || 0), 0);
    const totalLateMinutes = attendanceRecords.reduce((total, r) => total + getArrivalLateMinutes(r.check_in, r.expected_arrival), 0);
    const totalEarlyDepartureMinutes = attendanceRecords.reduce((total, r) => total + getEarlyDepartureMinutes(r.check_out, r.expected_leaving), 0);
    const totalExtraTimeMinutes = attendanceRecords.reduce((total, r) => total + getExtraTimeMinutes(r.check_out, r.expected_leaving), 0);

    return {
      totalWorkingDaysInMonth,
      totalExpectedMonthMinutes,
      totalAbsentDays,
      totalLeaveDaysInMonth,
      totalWorkedMinutes,
      workingDaysPassed,
      currentExpectedMinutes,
      expectedDailyMinutes,
      totalPauseMinutesMonth,
      totalLateMinutes,
      totalEarlyDepartureMinutes,
      totalExtraTimeMinutes,
      totalHolidaysInMonth,
      leaveDaysPassed,
    };
  };

  const downloadCSV = (data, filename) => {
    const headers = [
      'Employee Name', 'Email', 'Role', 'Field', 'Employment Type',
      'Month', 'Year', 'Working Days in Month', 'Total Expected Hours',
      'Absent Days', 'Leave Days', 'Holidays', 'Days Worked', 'Actual Hours',
      'Current Expected Hours', 'Late Minutes', 'Early Departure Minutes',
      'Extra Time Minutes', 'Outside Office Time',
    ];

    const rows = data.map(({ employee, stats, month, year }) => {
      return [
        `"${employee.employeeName || ''}"`,
        `"${employee.email || ''}"`,
        `"${employee.role || 'employee'}"`,
        `"${employee.employee_fields || ''}"`,
        `"${employee.employment_type === 'full_time' ? 'Full Time' : 'Half Time'}"`,
        `"${month}"`,
        year,
        stats.totalWorkingDaysInMonth || 0,
        formatHoursMins(stats.totalExpectedMonthMinutes || 0),
        stats.totalAbsentDays || 0,
        stats.totalLeaveDaysInMonth || 0,
        stats.totalHolidaysInMonth || 0,
        stats.workingDaysPassed - (stats.totalAbsentDays || 0),
        formatHoursMins(stats.totalWorkedMinutes || 0),
        formatHoursMins(stats.currentExpectedMinutes || 0),
        stats.totalLateMinutes || 0,
        stats.totalEarlyDepartureMinutes || 0,
        stats.totalExtraTimeMinutes || 0,
        formatHoursMins(stats.totalPauseMinutesMonth || 0),
      ];
    });

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ==================== Stats Email ====================
  const handleSendStatsEmail = async () => {
    if (!statsEmployee || !statsMonthFilter) return;
    const [year, month] = statsMonthFilter.split("-").map(Number);
    if (!window.confirm(`Send attendance report to ${statsEmployee.employeeName}'s email (${statsEmployee.email})?`)) return;

    try {
      setEmailSending(true);
      const token = getToken();
      const response = await api.post("/admin/send_stats_email", {
        employee_id: statsEmployee.id,
        month: month,
        year: year
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.status === 200) {
        setSuccessMsg(`Attendance report sent to ${statsEmployee.email} successfully!`);
        setTimeout(() => setSuccessMsg(""), 4000);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Failed to send email");
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setEmailSending(false);
    }
  };

  // ==================== Holidays ====================
  const handleGetHolidays = async () => {
    try {
      const token = getToken();
      const response = await api.get("/admin/holidays", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setHolidays(response.data.holidays || []);
      }
    } catch (error) {
      console.error("Failed to fetch holidays:", error);
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!holidayForm.holiday_date || !holidayForm.holiday_name) {
      setErrorMsg("Holiday date and name are required");
      return;
    }

    try {
      setHolidayLoading(true);
      const token = getToken();
      const response = await api.post(
        "/admin/holiday/add",
        {
          holiday_date: holidayForm.holiday_date,
          holiday_name: holidayForm.holiday_name,
          description: holidayForm.description || ""
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 201) {
        setSuccessMsg("Holiday added successfully!");
        setShowHolidayModal(false);
        setHolidayForm({ holiday_date: "", holiday_name: "", description: "" });
        handleGetHolidays();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Failed to add holiday");
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setHolidayLoading(false);
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    if (!window.confirm("Are you sure you want to delete this holiday?")) return;

    try {
      const token = getToken();
      const response = await api.delete(`/admin/holiday/delete?id=${holidayId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        setSuccessMsg("Holiday deleted successfully!");
        handleGetHolidays();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Failed to delete holiday");
      setTimeout(() => setErrorMsg(""), 4000);
    }
  };

  // ==================== Employee CRUD ====================
  const handleToggleEmployeeStatus = async (employeeId, employeeName, currentStatus) => {
    const newStatus = currentStatus == 1 ? 0 : 1;
    const action = newStatus == 1 ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} "${employeeName}"?`)) return;

    try {
      setActionLoading((prev) => ({ ...prev, [`status_${employeeId}`]: true }));
      const token = getToken();
      const response = await api.put(
        `/admin/update_employee_status/${employeeId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.status === 200) {
        setSuccessMsg(`${employeeName} ${action}d successfully!`);
        setEmployees((prev) =>
          prev.map((emp) =>
            emp.id === employeeId ? { ...emp, status: newStatus } : emp
          )
        );
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || `Failed to ${action} ${employeeName}`);
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setActionLoading((prev) => ({ ...prev, [`status_${employeeId}`]: false }));
    }
  };

  const handleGetEmployees = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      const response = await api.get("/admin/get_employees", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        const employeesData = response.data.employees || [];
        const processedEmployees = employeesData.map(emp => ({
          ...emp,
          status: emp.status !== undefined && emp.status !== null
            ? parseInt(emp.status)
            : 1
        }));
        setEmployees(processedEmployees);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Failed to fetch employees");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetTodayStatus = async () => {
    try {
      const token = getToken();
      const response = await api.get("/admin/get_today_status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200)
        setTodayStatus(response.data.todayStatus || {});
    } catch (error) {
      console.log(`Failed to fetch today status: ${error}`);
    }
  };

  const getAvailableYears = (attendanceRecords) => {
    const years = new Set();
    const currentYr = new Date().getFullYear();
    years.add(currentYr);
    if (attendanceRecords && attendanceRecords.length > 0) {
      attendanceRecords.forEach((r) => {
        if (r.check_in) {
          const d = new Date(r.check_in);
          years.add(d.getFullYear());
        }
      });
    }
    years.add(currentYr - 1);
    return Array.from(years).sort((a, b) => b - a);
  };

  const handleViewAttendanceReport = () => {
    if (!statsEmployee || !statsMonthFilter) return;
    const [year, month] = statsMonthFilter.split("-").map(Number);
    const totalDaysInMonth = new Date(year, month, 0).getDate();
    const firstDay = `${year}-${String(month).padStart(2, "0")}-01`;
    const lastDay = `${year}-${String(month).padStart(2, "0")}-${totalDaysInMonth}`;
    const employeeName = encodeURIComponent(statsEmployee.employeeName || '');
    navigate(`/admin/attendance?employee=${statsEmployee.id}&employeeName=${employeeName}&start=${firstDay}&end=${lastDay}`);
    setShowStatsModal(false);
  };

  // ==================== ✅ Special Intern Stats Handler ====================
  const handleViewSpecialStats = async (employee, selectedMonthYear = "") => {
    setStatsEmployee(employee);
    setShowStatsModal(true);
    setStatsLoading(true);
    setStatsData(null);
    setAbsentDates([]);
    setLeaveDatesList([]);
    setApplyExtraTime(false);

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    let statsMonth, statsYear;

    if (!selectedMonthYear) {
      statsMonth = currentMonth;
      statsYear = currentYear;
      selectedMonthYear = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}`;
    } else if (selectedMonthYear.includes("-")) {
      const [year, month] = selectedMonthYear.split("-").map(Number);
      statsYear = year;
      statsMonth = month - 1;
    } else {
      statsMonth = monthNames.indexOf(selectedMonthYear);
      statsYear = currentYear;
      if (statsMonth < 0) statsMonth = currentMonth;
      selectedMonthYear = `${statsYear}-${String(statsMonth + 1).padStart(2, "0")}`;
    }

    setStatsMonthFilter(selectedMonthYear);
    setStatsYearFilter(statsYear.toString());

    try {
      const token = getToken();
      const totalDaysInMonth = new Date(statsYear, statsMonth + 1, 0).getDate();
      const isCurrentMonth = statsMonth === currentMonth && statsYear === currentYear;
      const currentDay = isCurrentMonth ? now.getDate() : totalDaysInMonth;

      const firstDayOfMonth = `${statsYear}-${String(statsMonth + 1).padStart(2, "0")}-01`;
      const lastDayOfMonth = `${statsYear}-${String(statsMonth + 1).padStart(2, "0")}-${totalDaysInMonth}`;

      const [attRes, holRes, leaveRes] = await Promise.all([
        api.get("/admin/filter_attendance", {
          params: {
            employee_id: employee.id,
            start_date: firstDayOfMonth,
            end_date: lastDayOfMonth,
          },
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get("/admin/holidays", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get(`/admin/leave_requests?status=approved`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const attendanceRecords = attRes.data.attendances || [];
      const holidaysList = holRes.data.holidays || [];
      const leavesList = leaveRes.data.leaves || [];

      const holidayDates = new Set(holidaysList.map((h) => h.holiday_date));
      const employeeLeaves = leavesList.filter((l) => l.employee_id == employee.id);
      const leaveDates = new Set(
        employeeLeaves
          .map((l) => parseDateSafe(l.leave_date))
          .filter(Boolean)
          .map((p) => p.dateStr)
      );

      const attByDate = {};
      attendanceRecords.forEach((r) => {
        if (!r.check_in) return;
        const d = new Date(r.check_in);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        attByDate[dateStr] = r;
      });

      const allAbsentDates = [];
      let totalWorkingDaysInMonth = 0;
      let workingDaysPassed = 0;
      let totalWorkedMinutes = 0;
      let totalLeaveDaysInMonth = 0;
      let totalHolidaysInMonth = 0;
      let totalAbsentDays = 0;

      const weeklyData = {};

      for (let d = 1; d <= totalDaysInMonth; d++) {
        const dateObj = new Date(statsYear, statsMonth, d);
        const dayOfWeek = dateObj.getDay();
        const dateStr = `${statsYear}-${String(statsMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

        const dayOffset = (dayOfWeek + 6) % 7;
        const weekStart = new Date(dateObj);
        weekStart.setDate(weekStart.getDate() - dayOffset);
        const weekKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, "0")}-${String(weekStart.getDate()).padStart(2, "0")}`;
        if (!weeklyData[weekKey]) {
          weeklyData[weekKey] = {
            weekLabel: `Week of ${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
            weekStartDate: weekKey,
            workedMinutes: 0,
            daysWorked: 0,
            expectedDays: 0,
          };
        }

        if (isSpecialOffDay(dayOfWeek)) continue;

        if (holidayDates.has(dateStr)) {
          totalHolidaysInMonth++;
          continue;
        }

        if (leaveDates.has(dateStr)) {
          totalLeaveDaysInMonth++;
          continue;
        }

        totalWorkingDaysInMonth++;
        weeklyData[weekKey].expectedDays++;

        const isPast = d <= currentDay;
        const rec = attByDate[dateStr];

        if (isPast) {
          workingDaysPassed++;

          if (rec) {
            const windowMinutes = getSpecialWindowMinutes(rec.check_in, rec.check_out);
            if (windowMinutes > 0) {
              totalWorkedMinutes += windowMinutes;
              weeklyData[weekKey].workedMinutes += windowMinutes;
              weeklyData[weekKey].daysWorked++;
            } else {
              totalAbsentDays++;
              allAbsentDates.push({
                day: d,
                date: dateStr,
                dayName: dateObj.toLocaleDateString("en-US", { weekday: "short" }),
              });
            }
          } else {
            totalAbsentDays++;
            allAbsentDates.push({
              day: d,
              date: dateStr,
              dayName: dateObj.toLocaleDateString("en-US", { weekday: "short" }),
            });
          }
        }
      }

      const weeklySummary = Object.values(weeklyData)
        .filter((w) => w.expectedDays > 0)
        .map((w) => {
          const shortfallMinutes = Math.max(0, INTERN_SPECIAL_WEEKLY_MIN_MINUTES - w.workedMinutes);
          const extraMinutes = Math.max(0, w.workedMinutes - INTERN_SPECIAL_WEEKLY_MIN_MINUTES);
          const meetsMinimum = w.workedMinutes >= INTERN_SPECIAL_WEEKLY_MIN_MINUTES;
          return {
            weekLabel: w.weekLabel,
            weekStartDate: w.weekStartDate,
            workedMinutes: w.workedMinutes,
            expectedMinutes: INTERN_SPECIAL_WEEKLY_MIN_MINUTES,
            daysWorked: w.daysWorked,
            expectedDays: w.expectedDays,
            meetsMinimum,
            shortfallMinutes,
            extraMinutes,
          };
        })
        .sort((a, b) => a.weekStartDate.localeCompare(b.weekStartDate));

      const weeksInMonth = weeklySummary.length;
      const totalExpectedMonthMinutes = weeksInMonth * INTERN_SPECIAL_WEEKLY_MIN_MINUTES;
      const extraMinutes = weeklySummary.reduce((sum, w) => sum + w.extraMinutes, 0);
      const shortfallMinutes = weeklySummary.reduce((sum, w) => sum + w.shortfallMinutes, 0);
      const absentMinutes = totalAbsentDays * INTERN_SPECIAL_DAILY_EXPECTED_MINUTES;
      const leaveMinutes = totalLeaveDaysInMonth * INTERN_SPECIAL_DAILY_EXPECTED_MINUTES;

      const availableYears = getAvailableYears(attendanceRecords);

      setAbsentDates(allAbsentDates);

      const leaveDaysList = employeeLeaves
        .map((leave) => parseDateSafe(leave.leave_date))
        .filter((parsed) => {
          if (!parsed) return false;
          if (parsed.year !== statsYear) return false;
          if (parsed.month !== statsMonth + 1) return false;
          const dateObj = parsed.toDateObj();
          return !holidayDates.has(parsed.dateStr) && !isSpecialOffDay(dateObj.getDay());
        })
        .map((parsed) => ({
          day: parsed.day,
          date: parsed.dateStr,
          dayName: parsed.toDateObj().toLocaleDateString("en-US", { weekday: "short" }),
        }))
        .sort((a, b) => a.day - b.day);
      setLeaveDatesList(leaveDaysList);

      // ✅ NEW: Get badge times from attendance records (snapshot)
      const firstRecWithExpected = attendanceRecords.find(
        (r) => r.expected_arrival && r.expected_leaving
      );
      const badgeArrivalTime = firstRecWithExpected?.expected_arrival || employee.expected_arrival || null;
      const badgeLeavingTime = firstRecWithExpected?.expected_leaving || employee.expected_leaving || null;

      setStatsData({
        isSpecialRule: true,
        totalWorkingDaysInMonth,
        workingDaysPassed,
        totalLeaveDaysInMonth,
        totalHolidaysInMonth,
        totalAbsentDays,
        totalWorkedMinutes,
        totalExpectedMonthMinutes,
        extraMinutes,
        shortfallMinutes,
        absentMinutes,
        leaveMinutes,
        weeksInMonth,
        weeklySummary,
        weeklyMinMinutes: INTERN_SPECIAL_WEEKLY_MIN_MINUTES,
        dailyExpectedMinutes: INTERN_SPECIAL_DAILY_EXPECTED_MINUTES,
        expectedDailyMinutes: INTERN_SPECIAL_DAILY_EXPECTED_MINUTES,
        totalLateMinutes: 0,
        totalEarlyDepartureMinutes: 0,
        totalExtraTimeMinutes: extraMinutes,
        totalPauseMinutesMonth: 0,
        employeeRole: "intern_special",
        availableYears,
        badgeArrivalTime,
        badgeLeavingTime,
      });
    } catch (error) {
      console.log("Failed to fetch special intern stats:", error);
      setErrorMsg("Failed to load employee statistics");
      setTimeout(() => setErrorMsg(""), 3000);
    } finally {
      setStatsLoading(false);
    }
  };

  // ==================== ✅ Normal Stats Handler ====================
  const handleViewStats = async (employee, selectedMonthYear = "") => {
    if (String(employee.rule_type || "").toLowerCase() === "intern_special") {
      return handleViewSpecialStats(employee, selectedMonthYear);
    }
    setStatsEmployee(employee);
    setShowStatsModal(true);
    setStatsLoading(true);
    setStatsData(null);
    setAbsentDates([]);
    setLeaveDatesList([]);
    setApplyExtraTime(false);

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    let statsMonth, statsYear;

    if (!selectedMonthYear) {
      statsMonth = currentMonth;
      statsYear = currentYear;
      selectedMonthYear = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}`;
    } else if (selectedMonthYear.includes("-")) {
      const [year, month] = selectedMonthYear.split("-").map(Number);
      statsYear = year;
      statsMonth = month - 1;
    } else {
      statsMonth = monthNames.indexOf(selectedMonthYear);
      statsYear = currentYear;
      if (statsMonth < 0) statsMonth = currentMonth;
      selectedMonthYear = `${statsYear}-${String(statsMonth + 1).padStart(2, "0")}`;
    }

    setStatsMonthFilter(selectedMonthYear);
    setStatsYearFilter(statsYear.toString());

    try {
      const token = getToken();
      const totalDaysInMonth = new Date(statsYear, statsMonth + 1, 0).getDate();
      const isCurrentMonth = statsMonth === currentMonth && statsYear === currentYear;
      const currentDay = isCurrentMonth ? now.getDate() : totalDaysInMonth;
      const employeeRole = employee.role || "employee";

      const firstDayOfMonth = `${statsYear}-${String(statsMonth + 1).padStart(2, "0")}-01`;
      const lastDayOfMonth = `${statsYear}-${String(statsMonth + 1).padStart(2, "0")}-${totalDaysInMonth}`;

      const response = await api.get("/admin/filter_attendance", {
        params: {
          employee_id: employee.id,
          start_date: firstDayOfMonth,
          end_date: lastDayOfMonth,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        const attendanceRecords = response.data.attendances || [];

        const holidaysResponse = await api.get("/admin/holidays", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const holidaysList = holidaysResponse.data.holidays || [];
        const holidayDates = new Set(holidaysList.map(h => h.holiday_date));

        const leavesResponse = await api.get(`/admin/leave_requests?status=approved`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const leavesList = leavesResponse.data.leaves || [];
        const employeeLeaves = leavesList.filter(l => l.employee_id == employee.id);

        const leaveDates = new Set(
          employeeLeaves
            .map((l) => parseDateSafe(l.leave_date))
            .filter(Boolean)
            .map((p) => p.dateStr)
        );

        if (attendanceRecords.length === 0 && employeeLeaves.length === 0) {
          setStatsData(null);
          setAbsentDates([]);
          setLeaveDatesList([]);
          setStatsLoading(false);
          return;
        }

        let workingDaysPassed = 0;
        let holidayDaysPassed = 0;
        let leaveDaysPassed = 0;
        const allWorkingDaysPassed = [];

        for (let d = 1; d <= currentDay; d++) {
          const dateObj = new Date(statsYear, statsMonth, d);
          const dayOfWeek = dateObj.getDay();
          const dateStr = `${statsYear}-${String(statsMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

          if (!isOffDay(dayOfWeek, employeeRole)) {
            if (holidayDates.has(dateStr)) {
              holidayDaysPassed++;
            } else if (leaveDates.has(dateStr)) {
              leaveDaysPassed++;
            } else {
              workingDaysPassed++;
              allWorkingDaysPassed.push({
                day: d,
                date: dateStr,
                dayName: dateObj.toLocaleDateString("en-US", { weekday: "short" }),
              });
            }
          }
        }

        let totalWorkingDaysInMonth = 0;
        let totalHolidaysInMonth = 0;
        let totalLeaveDaysInMonth = 0;

        for (let d = 1; d <= totalDaysInMonth; d++) {
          const dayOfWeek = new Date(statsYear, statsMonth, d).getDay();
          const dateStr = `${statsYear}-${String(statsMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

          if (!isOffDay(dayOfWeek, employeeRole)) {
            if (holidayDates.has(dateStr)) {
              totalHolidaysInMonth++;
            } else if (leaveDates.has(dateStr)) {
              totalLeaveDaysInMonth++;
            } else {
              totalWorkingDaysInMonth++;
            }
          }
        }

        const workingDaysSet = new Set(
          attendanceRecords
            .map((r) => r.check_in ? new Date(r.check_in).toISOString().split("T")[0] : null)
            .filter(Boolean),
        );

        const absentDaysList = allWorkingDaysPassed.filter(
          (day) => !workingDaysSet.has(day.date) && !holidayDates.has(day.date) && !leaveDates.has(day.date),
        );
        setAbsentDates(absentDaysList);

        const leaveDaysList = employeeLeaves
          .map((leave) => parseDateSafe(leave.leave_date))
          .filter((parsed) => {
            if (!parsed) return false;
            if (parsed.year !== statsYear) return false;
            if (parsed.month !== statsMonth + 1) return false;
            const dateObj = parsed.toDateObj();
            const dayOfWeek = dateObj.getDay();
            const dateStr = parsed.dateStr;
            return (
              !holidayDates.has(dateStr) &&
              !isOffDay(dayOfWeek, employeeRole)
            );
          })
          .map((parsed) => {
            const dateObj = parsed.toDateObj();
            return {
              day: parsed.day,
              date: parsed.dateStr,
              dayName: dateObj.toLocaleDateString("en-US", { weekday: "short" }),
            };
          })
          .sort((a, b) => a.day - b.day);
        setLeaveDatesList(leaveDaysList);

        const totalAbsentDays = absentDaysList.length;

        let expectedDailyMinutes = 0;
        let badgeArrivalTime = null;
        let badgeLeavingTime = null;

        const firstWithExpected = attendanceRecords.find(
          (r) => r.expected_arrival && r.expected_leaving,
        );
        if (firstWithExpected) {
          expectedDailyMinutes = getExpectedDailyMinutes(
            firstWithExpected.expected_arrival,
            firstWithExpected.expected_leaving,
          );
          badgeArrivalTime = firstWithExpected.expected_arrival;
          badgeLeavingTime = firstWithExpected.expected_leaving;
        } else if (employee.expected_arrival && employee.expected_leaving) {
          expectedDailyMinutes = getExpectedDailyMinutes(
            employee.expected_arrival,
            employee.expected_leaving,
          );
          badgeArrivalTime = employee.expected_arrival;
          badgeLeavingTime = employee.expected_leaving;
        }

        const totalWorkingDaysForExpected = totalWorkingDaysInMonth + (totalLeaveDaysInMonth || 0);
        const totalExpectedMonthMinutes = totalWorkingDaysForExpected * expectedDailyMinutes;
        const actualDaysWorked = workingDaysPassed - totalAbsentDays;
        const currentExpectedMinutes = actualDaysWorked * expectedDailyMinutes;

        const totalWorkedMinutes = attendanceRecords.reduce((total, r) => {
          const grossMinutes = getWorkingMinutes(r.check_in, r.check_out);
          const pauseMinutes = r.total_pause_minutes || 0;
          return total + Math.max(0, grossMinutes - pauseMinutes);
        }, 0);

        const totalPauseMinutesMonth = attendanceRecords.reduce(
          (total, r) => total + (r.total_pause_minutes || 0), 0,
        );
        const totalGrossMinutes = attendanceRecords.reduce(
          (total, r) => total + getWorkingMinutes(r.check_in, r.check_out), 0,
        );

        const totalLateMinutes = attendanceRecords.reduce((total, r) => {
          return total + getArrivalLateMinutes(r.check_in, r.expected_arrival);
        }, 0);

        const totalEarlyDepartureMinutes = attendanceRecords.reduce((total, r) => {
          return total + getEarlyDepartureMinutes(r.check_out, r.expected_leaving);
        }, 0);

        const totalExtraTimeMinutes = attendanceRecords.reduce((total, r) => {
          return total + getExtraTimeMinutes(r.check_out, r.expected_leaving);
        }, 0);

        const availableYears = getAvailableYears(attendanceRecords);

        setStatsData({
          totalWorkingDaysInMonth,
          totalExpectedMonthMinutes,
          totalAbsentDays,
          totalWorkedMinutes,
          workingDaysPassed,
          currentExpectedMinutes,
          expectedDailyMinutes,
          employeeRole,
          totalPauseMinutesMonth,
          totalGrossMinutes,
          totalLateMinutes,
          totalEarlyDepartureMinutes,
          totalExtraTimeMinutes,
          availableYears,
          totalHolidaysInMonth,
          holidayDaysPassed,
          totalLeaveDaysInMonth,
          leaveDaysPassed,
          badgeArrivalTime,
          badgeLeavingTime,
        });
      }
    } catch (error) {
      console.log("Failed to fetch employee stats:", error);
      setErrorMsg("Failed to load employee statistics");
      setTimeout(() => setErrorMsg(""), 3000);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleStatsMonthChange = (value) => {
    setApplyExtraTime(false);
    if (statsEmployee && value) {
      handleViewStats(statsEmployee, value);
    }
  };

  const isFutureMonth = (year, monthIndex) => {
    const now = new Date();
    if (year > now.getFullYear()) return true;
    if (year === now.getFullYear() && monthIndex > now.getMonth()) return true;
    return false;
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    setAddTimeError("");
    if (
      !formData.name || !formData.email || !formData.cnic || !formData.employmentType
    ) {
      setErrorMsg("All fields except password are required");
      return;
    }
    if (!isValidCNIC(formData.cnic)) {
      setErrorMsg("Invalid CNIC format. Use: 12345-1234567-1");
      return;
    }
    if (formData.expectedArrival && formData.expectedLeaving) {
      const timeError = validateTimeOrder(formData.expectedArrival, formData.expectedLeaving);
      if (timeError) {
        setAddTimeError(timeError);
        return;
      }
    }
    try {
      setIsLoading(true);
      const token = getToken();
      const response = await api.post(
        "/admin/add_employee",
        {
          name: formData.name,
          email: formData.email,
          cnic: formData.cnic,
          employmentType: formData.employmentType,
          employeeFields: formData.employeeField,
          role: formData.role,
          expectedArrival: formData.expectedArrival || null,
          expectedLeaving: formData.expectedLeaving || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.status === 201) {
        setSuccessMsg("Employee added successfully! They can activate their account.");
        setShowAddModal(false);
        resetForm();
        handleGetEmployees();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (error) {
      setErrorMsg(
        error.response?.status === 409
          ? "Email or CNIC already exists"
          : error.response?.data?.message || "Failed to add employee",
      );
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "cnic") setFormData({ ...formData, cnic: formatCNIC(value) });
    else setFormData({ ...formData, [name]: value });
    if (name === "expectedArrival" || name === "expectedLeaving") {
      setAddTimeError("");
      setEditTimeError("");
    }
  };

  const handleAddTimeChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    setAddTimeError("");
    if (updatedFormData.expectedArrival && updatedFormData.expectedLeaving) {
      const timeError = validateTimeOrder(updatedFormData.expectedArrival, updatedFormData.expectedLeaving);
      if (timeError) setAddTimeError(timeError);
    }
  };

  const handleEditTimeChange = (e) => {
    const { name, value } = e.target;
    const updatedEditForm = { ...editForm, [name]: value };
    setEditForm(updatedEditForm);
    setEditTimeError("");
    if (updatedEditForm.expectedArrival && updatedEditForm.expectedLeaving) {
      const timeError = validateTimeOrder(updatedEditForm.expectedArrival, updatedEditForm.expectedLeaving);
      if (timeError) setEditTimeError(timeError);
    }
  };

  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    cnic: "",
    employmentType: "",
    employeeField: "",
    role: "employee",
    password: "",
    expectedArrival: "",
    expectedLeaving: "",
  });

  const handleOpenEditModal = (employee) => {
    setEditingEmployee(employee);
    setEditForm({
      name: employee.employeeName || "",
      email: employee.email || "",
      cnic: employee.cnic || "",
      employmentType: employee.employment_type || "",
      employeeField: employee.employee_fields || "",
      role: employee.role || "employee",
      password: "",
      expectedArrival: employee.expected_arrival || "",
      expectedLeaving: employee.expected_leaving || "",
    });
    setEditTimeError("");
    setShowEditModal(true);
    setErrorMsg("");
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    setEditTimeError("");
    if (!editForm.name || !editForm.email || !editForm.employmentType) {
      setErrorMsg("Name, email, and employment type are required");
      return;
    }
    if (!isValidCNIC(editForm.cnic)) {
      setErrorMsg("Invalid CNIC format. Use: 12345-1234567-1");
      return;
    }
    if (editForm.expectedArrival && editForm.expectedLeaving) {
      const timeError = validateTimeOrder(editForm.expectedArrival, editForm.expectedLeaving);
      if (timeError) {
        setEditTimeError(timeError);
        return;
      }
    }
    try {
      setIsLoading(true);
      const token = getToken();
      const payload = {
        name: editForm.name,
        email: editForm.email,
        cnic: editForm.cnic,
        employmentType: editForm.employmentType,
        employeeFields: editForm.employeeField,
        role: editForm.role,
        expectedArrival: editForm.expectedArrival || null,
        expectedLeaving: editForm.expectedLeaving || null,
      };
      if (editForm.password) payload.password = editForm.password;
      const response = await api.put(
        `/admin/update_employee/${editingEmployee.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.status === 200) {
        setSuccessMsg("Employee updated successfully!");
        setShowEditModal(false);
        setEditingEmployee(null);
        resetForm();
        handleGetEmployees();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (error) {
      setErrorMsg(
        error.response?.status === 409
          ? "Email or CNIC already used"
          : error.response?.data?.message || "Failed to update",
      );
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to delete "${employeeName}"?`)) return;
    try {
      setIsLoading(true);
      const token = getToken();
      const response = await api.delete(
        `/admin/delete_employee/${employeeId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.status === 200) {
        setSuccessMsg(`${employeeName} deleted successfully!`);
        setEmployees((prev) => prev.filter((emp) => emp.id !== employeeId));
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Failed to delete employee");
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminCheckIn = async (employeeId, employeeName) => {
    try {
      setActionLoading((prev) => ({ ...prev, [`checkin_${employeeId}`]: true }));
      setErrorMsg("");
      setSuccessMsg("");
      const token = getToken();
      const response = await api.post(
        "/admin/employee_check_in",
        { employee_id: employeeId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.status === 201) {
        setSuccessMsg(`${employeeName} checked in successfully!`);
        setTodayStatus((prev) => ({
          ...prev,
          [employeeId]: {
            ...prev[employeeId],
            checked_in: true,
            check_in_time: new Date(),
          },
        }));
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || `Failed to check in ${employeeName}`);
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setActionLoading((prev) => ({ ...prev, [`checkin_${employeeId}`]: false }));
    }
  };

  const handleAdminCheckOut = async (employeeId, employeeName) => {
    try {
      setActionLoading((prev) => ({ ...prev, [`checkout_${employeeId}`]: true }));
      setErrorMsg("");
      setSuccessMsg("");
      const token = getToken();
      const response = await api.post(
        "/admin/employee_check_out",
        { employee_id: employeeId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.status === 200) {
        setSuccessMsg(`${employeeName} checked out successfully!`);
        setTodayStatus((prev) => ({
          ...prev,
          [employeeId]: {
            ...prev[employeeId],
            checked_out: true,
            check_out_time: new Date(),
          },
        }));
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || `Failed to check out ${employeeName}`);
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setActionLoading((prev) => ({ ...prev, [`checkout_${employeeId}`]: false }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      cnic: "",
      employmentType: "",
      employeeField: "",
      role: "employee",
      password: "",
      expectedArrival: "",
      expectedLeaving: "",
    });
    setAddTimeError("");
  };

  const formatTime = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getOffDaysDescription = (role) => {
    const normalizedRole = String(role || "").toLowerCase();
    if (normalizedRole === "intern_special") return "Sunday only";
    if (normalizedRole === "intern" || normalizedRole === "student")
      return "Sundays & Saturdays";
    return "Sundays only";
  };

  useEffect(() => {
    handleGetEmployees();
    handleGetTodayStatus();
    handleGetHolidays();
  }, []);

  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch =
      emp.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.employee_fields?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === "all" || emp.role === roleFilter;

    const empStatus = emp.status !== undefined && emp.status !== null ? parseInt(emp.status) : 1;
    const matchesStatus = statusFilter === "all" ||
      (statusFilter === "active" && empStatus === 1) ||
      (statusFilter === "deactive" && empStatus === 0);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const employeeCount = employees.filter((emp) => emp.role === "employee").length;
  const internCount = employees.filter((emp) => emp.role === "intern").length;
  const studentCount = employees.filter((emp) => emp.role === "student").length;

  // ✅ NEW: Filter holidays by selected month/year
  const filteredHolidays = holidays.filter((h) => {
    const parsed = parseDateSafe(h.holiday_date);
    if (!parsed) return false;
    return parsed.year === holidayYearFilter && parsed.month === holidayMonthFilter;
  });

  // ✅ NEW: Available years for holiday filter
  const availableHolidayYears = (() => {
    const yearsSet = new Set();
    yearsSet.add(now.getFullYear());
    holidays.forEach((h) => {
      const parsed = parseDateSafe(h.holiday_date);
      if (parsed) yearsSet.add(parsed.year);
    });
    yearsSet.add(now.getFullYear() - 1);
    return Array.from(yearsSet).sort((a, b) => b - a);
  })();

  return (
    <div className="admin-container">
      <div className="header-banner mb-4">
        <div className="header-content">
          <div>
            <h1 className="header-title">Member Management</h1>
            <p className="header-subtitle">
              Manage employees, interns, and students
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className="btn-add-student"
              onClick={handleDownloadAllMembersCSV}
              disabled={csvDownloading || filteredEmployees.length === 0}
              style={{ background: '#FFFFFF', color: '#667EEA', opacity: csvDownloading ? 0.6 : 1 }}
              title="Download all members' current month stats"
            >
              <span className="btn-icon" style={{ display: 'inline-flex', alignItems: 'center' }}><FaDownload /></span> {csvDownloading ? "Downloading..." : "Download CSV"}
            </button>
            <button
              className="btn-add-student"
              onClick={handleGenerateShareLink}
              disabled={generatingLink}
              style={{ background: '#FFFFFF', color: '#667EEA', opacity: generatingLink ? 0.6 : 1 }}
              title="Generate a public link to share employee statistics"
            >
              <span className="btn-icon" style={{ display: 'inline-flex', alignItems: 'center' }}><FaLink /></span> {generatingLink ? "Generating..." : "Share Link"}
            </button>
            <button
              className="btn-add-student"
              onClick={() => {
                setShowLeaveRequestsModal(true);
                handleGetLeaveRequests('pending');
              }}
              style={{ background: '#FFFFFF', color: '#667EEA' }}
            >
              <span className="btn-icon" style={{ display: 'inline-flex', alignItems: 'center' }}><FaClipboardList /></span> Leave Requests
            </button>
            <button
              className="btn-add-student"
              onClick={() => setShowHolidayModal(true)}
            >
              <span className="btn-icon" style={{ display: 'inline-flex', alignItems: 'center' }}><FaCalendarAlt /></span> Holidays
            </button>
            <button
              className="btn-add-student"
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
            >
              <span className="btn-icon" style={{ display: 'inline-flex', alignItems: 'center' }}><FaPlus /></span> Add Member
            </button>
          </div>
        </div>
      </div>
      {successMsg && <div className="success-message">{successMsg}</div>}
      {errorMsg && <div className="error-message">{errorMsg}</div>}

      {/* Holiday List Section */}
      {holidays.length > 0 && (
        <div className="students-card" style={{ marginBottom: '20px' }}>
          <div className="card-header-custom" style={{ flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 className="card-title">Holidays</h3>
              <p className="card-subtitle">
                {filteredHolidays.length} holidays in {monthNames[holidayMonthFilter - 1]} {holidayYearFilter}
              </p>
            </div>
            {/* ✅ NEW: Month/Year filter dropdowns */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                className="form-input filter-select"
                value={holidayMonthFilter}
                onChange={(e) => setHolidayMonthFilter(parseInt(e.target.value))}
                style={{ minWidth: "130px" }}
              >
                {monthNames.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
              <select
                className="form-input filter-select"
                value={holidayYearFilter}
                onChange={(e) => setHolidayYearFilter(parseInt(e.target.value))}
                style={{ minWidth: "100px" }}
              >
                {availableHolidayYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ padding: '16px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {filteredHolidays.length > 0 ? (
              filteredHolidays.map((holiday) => (
                <div key={holiday.id} style={{
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', color: '#166534' }}>{holiday.holiday_name}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      {new Date(holiday.holiday_date).toLocaleDateString("en-US", {
                        weekday: "short", month: "short", day: "numeric", year: "numeric"
                      })}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteHoliday(holiday.id)}
                    style={{
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    title="Delete holiday"
                    aria-label="Delete holiday"
                  >
                    <FaTimes />
                  </button>
                </div>
              ))
            ) : (
              <div style={{ width: '100%', textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '13px' }}>
                No holidays in {monthNames[holidayMonthFilter - 1]} {holidayYearFilter}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="students-card">
        <div className="card-header-custom">
          <div>
            <h3 className="card-title">All Members</h3>
            <p className="card-subtitle">
              {filteredEmployees.length} of {employees.length} members (
              {employeeCount} Emp, {internCount} Int, {studentCount} Std)
            </p>
          </div>
          <div className="header-actions">
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="form-input filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="deactive">Deactive</option>
            </select>
          </div>
        </div>

        <div className="table-wrapper">
          {isLoading ? (
            <div style={{ textAlign: "center", padding: "60px" }}>
              <div className="loading-spinner"></div>
              <p style={{ marginTop: "15px", color: "#888" }}>Loading...</p>
            </div>
          ) : (
            <table className="students-table employee-management-table">
              <thead>
                <tr>
                  <th className="col-emp-sr">Sr#</th>
                  <th className="col-emp-name">Name</th>
                  <th className="col-emp-role">Role</th>
                  <th className="col-emp-field">Field</th>
                  <th className="col-emp-type">Type</th>
                  <th className="col-emp-status">Today</th>
                  <th className="col-emp-stats">Stats</th>
                  <th className="col-emp-attendance">Actions</th>
                  <th className="col-emp-chat">Chat</th>
                  <th className="col-emp-manage">Status</th>
                  <th className="col-emp-manage">Manage</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.length > 0 ? (
                  filteredEmployees.map((employee, index) => {
                    const empStatus = todayStatus[employee.id] || {};
                    const isCheckedIn = empStatus.checked_in;
                    const isCheckedOut = empStatus.checked_out;
                    const employeeActiveStatus = employee.status !== undefined && employee.status !== null
                      ? parseInt(employee.status)
                      : 1;
                    const isActive = employeeActiveStatus === 1;

                    return (
                      <tr key={employee.id} style={{ opacity: isActive ? 1 : 0.6 }}>
                        <td className="col-emp-sr" style={{ textAlign: "center", fontWeight: "600", color: "#64748b" }}>
                          {index + 1}
                        </td>
                        <td className="col-emp-name">
                          <div className="student-info">
                            <div className="student-avatar">
                              {employee.employeeName?.charAt(0) || "E"}
                            </div>
                            <span className="employee-name-text">
                              {employee.employeeName}
                            </span>
                          </div>
                        </td>
                        <td className="col-emp-role">
                          <span className={`role-badge role-${employee.role || "employee"}`}>
                            {employee.role === "intern" ? "Intern" : employee.role === "student" ? "Student" : "Employee"}
                          </span>
                        </td>
                        <td className="col-emp-field">
                          <span className="field-badge">{employee.employee_fields || "—"}</span>
                        </td>
                        <td className="col-emp-type">
                          <span className={`type-badge type-${employee.employment_type}`}>
                            {employee.employment_type === "full_time" ? "Full" : "Half"}
                          </span>
                        </td>
                        <td className="col-emp-status">
                          {isCheckedOut ? (
                            <div><span className="status-badge status-present">Done</span><div className="time-detail"><small>{formatTime(empStatus.check_in_time)} - {formatTime(empStatus.check_out_time)}</small></div></div>
                          ) : isCheckedIn ? (
                            <div><span className="status-badge status-late">In</span><div className="time-detail"><small>{formatTime(empStatus.check_in_time)}</small></div></div>
                          ) : (
                            <span className="status-badge status-absent">Pending</span>
                          )}
                        </td>
                        <td className="col-emp-stats">
                          <button className="btn-action btn-stats" onClick={() => handleViewStats(employee)} title={`View ${employee.employeeName}'s attendance stats`}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><FaChartBar /> Stats</span>
                          </button>
                        </td>
                        <td className="col-emp-attendance">
                          <div className="action-buttons-group">
                            {isActive && !isCheckedOut && (
                              <>
                                <button className={`btn-action btn-checkin ${isCheckedIn ? "btn-disabled" : ""}`} onClick={() => handleAdminCheckIn(employee.id, employee.employeeName)} disabled={isCheckedIn || actionLoading[`checkin_${employee.id}`]}>
                                  {actionLoading[`checkin_${employee.id}`] ? <span className="mini-spinner"></span> : isCheckedIn ? "✓ In" : "In"}
                                </button>
                                {isCheckedIn && (
                                  <button className="btn-action btn-checkout" onClick={() => handleAdminCheckOut(employee.id, employee.employeeName)} disabled={actionLoading[`checkout_${employee.id}`]}>
                                    {actionLoading[`checkout_${employee.id}`] ? <span className="mini-spinner"></span> : "Out"}
                                  </button>
                                )}
                              </>
                            )}
                            {isCheckedOut && <span className="completed-text">✓ Done</span>}
                            {!isActive && <span className="completed-text" style={{ color: '#dc2626' }}>Deactivated</span>}
                          </div>
                        </td>
                        <td className="col-emp-chat">
                          <button className="btn-action btn-chat" onClick={() => navigate(`/chat?user=${employee.id}`)}>Chat</button>
                        </td>
                        <td className="col-emp-manage">
                          <button
                            className={`btn-action ${isActive ? "btn-delete" : "btn-checkin"}`}
                            onClick={() => handleToggleEmployeeStatus(employee.id, employee.employeeName, employeeActiveStatus)}
                            disabled={actionLoading[`status_${employee.id}`]}
                            title={isActive ? "Deactivate member" : "Activate member"}
                          >
                            {actionLoading[`status_${employee.id}`] ? (
                              <span className="mini-spinner"></span>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                {isActive ? <FaUserTimes style={{ color: 'var(--danger)' }} /> : <FaUserCheck style={{ color: 'var(--success)' }} />}
                                {isActive ? "Deactivate" : "Activate"}
                              </span>
                            )}
                          </button>
                        </td>
                        <td className="col-emp-manage">
                          <div className="manage-buttons">
                            <button className="btn-action btn-edit" onClick={() => handleOpenEditModal(employee)} title="Edit">Edit</button>
                            <button className="btn-action btn-delete" onClick={() => handleDeleteEmployee(employee.id, employee.employeeName)} title="Delete">Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr className="empty-state">
                    <td colSpan="11">
                      <div className="empty-state-content">
                        <div className="empty-icon" style={{ color: 'var(--primary)' }}><FaUsers /></div>
                        <p className="empty-text">No members found</p>
                        <p className="empty-subtext">{roleFilter !== "all" ? `No ${roleFilter}s found` : 'Click "Add Member" to get started.'}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* SHARE LINK MODAL */}
      {showShareLinkModal && (
        <div className="modal-overlay" onClick={() => setShowShareLinkModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px" }}>
            <div className="modal-header-custom">
              <div>
                <h3 className="modal-title">Public Share Link</h3>
                <p className="modal-subtitle">Anyone with this link can view employee statistics</p>
              </div>
              <button className="modal-close" onClick={() => setShowShareLinkModal(false)} aria-label="Close share link modal">
                <FaTimes />
              </button>
            </div>
            <div className="modal-body-custom">
              <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: "10px", padding: "12px 16px", marginBottom: "16px", fontSize: "13px", color: "#166534", display: "flex", alignItems: "center", gap: "8px" }}>
                <FaCheckCircle style={{ fontSize: "18px", color: "var(--success)" }} />
                <span>Link generated successfully. Share it with anyone who needs access.</span>
              </div>

              <label style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", marginBottom: "6px", display: "block", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Shareable Link
              </label>
              <div style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  onFocus={(e) => e.target.select()}
                  style={{ flex: 1, padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "12px", fontFamily: "monospace", background: "#f8fafc", color: "#1e293b", outline: "none" }}
                />
                <button onClick={handleCopyShareLink} style={{ background: "linear-gradient(135deg, #667eea, #764ba2)", color: "white", border: "none", padding: "10px 20px", borderRadius: "8px", fontWeight: "600", cursor: "pointer", whiteSpace: "nowrap", fontSize: "13px" }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><FaClipboardList /> Copy</span>
                </button>
              </div>

              <div style={{ marginTop: "16px", padding: "12px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", fontSize: "12px", color: "#92400e", display: "flex", gap: "8px", alignItems: "flex-start" }}>
                <FaInfoCircle style={{ fontSize: "14px", color: 'var(--warning)' }} />
                <span><strong>Note:</strong> This link shows all active members' statistics. Click on any employee in the list to view their detailed stats.</span>
              </div>

              <div style={{ marginTop: "16px", display: "flex", gap: "10px" }}>
                <button
                  onClick={() => { setShowShareLinkModal(false); window.open(shareLink, "_blank"); }}
                  style={{ flex: 1, padding: "10px", background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: "8px", fontWeight: "600", cursor: "pointer", fontSize: "13px" }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><FaEye /> Preview Link</span>
                </button>
                <button onClick={() => setShowShareLinkModal(false)} style={{ flex: 1, padding: "10px", background: "linear-gradient(135deg, #667eea, #764ba2)", color: "white", border: "none", borderRadius: "8px", fontWeight: "600", cursor: "pointer", fontSize: "13px" }}>
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LEAVE REQUESTS MODAL */}
      {showLeaveRequestsModal && (
        <div className="modal-overlay" onClick={() => setShowLeaveRequestsModal(false)}>
          <div className="modal-container stats-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <div>
                <h3 className="modal-title">Leave Requests</h3>
                <p className="modal-subtitle">Manage member leave requests</p>
              </div>
              <button className="modal-close" onClick={() => setShowLeaveRequestsModal(false)} aria-label="Close leave requests modal"><FaTimes /></button>
            </div>
            <div className="modal-body-custom">
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {['pending', 'approved', 'rejected'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => { setLeaveFilterTab(tab); handleGetLeaveRequests(tab); }}
                    style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px', background: leaveFilterTab === tab ? '#4f46e5' : '#f1f5f9', color: leaveFilterTab === tab ? 'white' : '#64748b', textTransform: 'capitalize' }}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {leaveRequestsLoading ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <div className="loading-spinner"></div>
                </div>
              ) : leaveRequests.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
                  {leaveRequests.map((leave) => (
                    <div key={leave.id} style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                          <strong style={{ fontSize: '15px', color: '#1e293b' }}>{leave.employeeName}</strong>
                          <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px', textTransform: 'capitalize' }}>{leave.member_role}</span>
                        </div>
                        <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: leave.status === 'approved' ? '#d4edda' : leave.status === 'rejected' ? '#f8d7da' : '#fff3cd', color: leave.status === 'approved' ? '#155724' : leave.status === 'rejected' ? '#721c24' : '#856404', textTransform: 'capitalize' }}>
                          {leave.status}
                        </span>
                      </div>
                      <div style={{ marginTop: '8px' }}>
                        <p style={{ margin: '4px 0', fontSize: '13px', color: '#475569' }}>
                          <strong>Date:</strong> {new Date(leave.leave_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        <p style={{ margin: '4px 0', fontSize: '13px', color: '#475569' }}>
                          <strong>Type:</strong> <span style={{ textTransform: 'capitalize' }}>{leave.leave_type}</span>
                        </p>
                        {leave.reason && (
                          <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
                            <strong>Reason:</strong> {leave.reason}
                          </p>
                        )}
                      </div>
                      {leave.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                          <button onClick={() => handleLeaveAction(leave.id, 'approve')} style={{ flex: 1, padding: '8px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><FaCheckCircle /> Approve</span>
                          </button>
                          <button onClick={() => handleLeaveAction(leave.id, 'reject')} style={{ flex: 1, padding: '8px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><FaTimes /> Reject</span>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <div style={{ fontSize: "48px", marginBottom: "12px", color: 'var(--primary)' }}><FaClipboardList /></div>
                  <p style={{ color: "#888", fontSize: "15px", fontWeight: "600" }}>No {leaveFilterTab} leave requests</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* HOLIDAY MODAL */}
      {showHolidayModal && (
        <div className="modal-overlay" onClick={() => setShowHolidayModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <div>
                <h3 className="modal-title">Announce Holiday</h3>
                <p className="modal-subtitle">Add a holiday date - members won't be marked absent</p>
              </div>
              <button className="modal-close" onClick={() => setShowHolidayModal(false)} aria-label="Close holiday modal"><FaTimes /></button>
            </div>
            <div className="modal-body-custom">
              <form onSubmit={handleAddHoliday}>
                <div className="form-group">
                  <label className="form-label">Holiday Date</label>
                  <input type="date" className="form-input" value={holidayForm.holiday_date} onChange={(e) => setHolidayForm({ ...holidayForm, holiday_date: e.target.value })} min={new Date().toISOString().split('T')[0]} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Holiday Name</label>
                  <input type="text" className="form-input" value={holidayForm.holiday_name} onChange={(e) => setHolidayForm({ ...holidayForm, holiday_name: e.target.value })} placeholder="e.g., Eid Holiday, Independence Day" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Description (Optional)</label>
                  <textarea style={{ resize: "none" }} className="form-input" value={holidayForm.description} onChange={(e) => setHolidayForm({ ...holidayForm, description: e.target.value })} rows="3" placeholder="Any additional details..." />
                </div>
                <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                  <button type="button" className="btn-cancel" onClick={() => setShowHolidayModal(false)}>Cancel</button>
                  <button type="submit" className="btn-submit" disabled={holidayLoading}>
                    {holidayLoading ? "Adding..." : "Add Holiday"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* STATS MODAL */}
      {showStatsModal && (
        <div className="modal-overlay" onClick={() => setShowStatsModal(false)}>
          <div className="modal-container stats-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <div>
                <h3 className="modal-title">Attendance Statistics</h3>
                <p className="modal-subtitle">
                  {statsEmployee?.employeeName}'s Monthly Overview
                  {statsData?.employeeRole && (
                    <span style={{ fontSize: "11px", color: "#888", marginLeft: "8px" }}>
                      (Off: {getOffDaysDescription(statsData.employeeRole)})
                    </span>
                  )}
                </p>
              </div>
              <button className="modal-close" onClick={() => setShowStatsModal(false)} aria-label="Close stats modal"><FaTimes /></button>
            </div>
            <div className="modal-body-custom">
              <div style={{ marginBottom: "12px", display: "flex", alignItems: "center", gap: "10px", marginTop: "-13px" }}>
                <span style={{ fontSize: "13px", fontWeight: "600", color: "#475569", whiteSpace: "nowrap" }}>Select Month:</span>
                <select className="form-input filter-select" value={statsMonthFilter} onChange={(e) => handleStatsMonthChange(e.target.value)} style={{ flex: 1 }}>
                  <option value="">Select...</option>
                  {statsData?.availableYears
                    ? statsData.availableYears.map((year) =>
                      monthNames.map((m, i) => {
                        const value = `${year}-${String(i + 1).padStart(2, "0")}`;
                        const disabled = isFutureMonth(year, i);
                        return (
                          <option key={value} value={value} disabled={disabled} style={disabled ? { color: "#cbd5e1" } : {}}>
                            {m} {year}{disabled ? " (Future)" : ""}
                          </option>
                        );
                      }),
                    )
                    : monthNames.map((m, i) => {
                      const value = `${new Date().getFullYear()}-${String(i + 1).padStart(2, "0")}`;
                      const disabled = isFutureMonth(new Date().getFullYear(), i);
                      return (
                        <option key={value} value={value} disabled={disabled} style={disabled ? { color: "#cbd5e1" } : {}}>
                          {m} {new Date().getFullYear()}{disabled ? " (Future)" : ""}
                        </option>
                      );
                    })}
                </select>
              </div>

              {/* ✅ Badges + Action buttons — single row */}
              {statsData && (
                <div style={{
                  marginBottom: "15px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "nowrap",
                }}>
                  {/* Left: Expected Arrival / Departure badges — from statsData.badgeArrivalTime / badgeLeavingTime */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                    {statsData.badgeArrivalTime && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                        border: '1px solid #93c5fd',
                        color: '#1e40af',
                        fontSize: '12px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        <span style={{ color: '#64748b', fontWeight: '500' }}>Check In:</span>
                        <span>{formatTimeString(statsData.badgeArrivalTime)}</span>
                      </div>
                    )}
                    {statsData.badgeLeavingTime && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                        border: '1px solid #fcd34d',
                        color: '#92400e',
                        fontSize: '12px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        <span style={{ color: '#64748b', fontWeight: '500' }}>Check Out:</span>
                        <span>{formatTimeString(statsData.badgeLeavingTime)}</span>
                      </div>
                    )}
                  </div>

                  {/* Right: action buttons */}
                  <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
                    <button
                      className="btn-add-student"
                      onClick={handleDownloadSingleMemberCSV}
                      style={{ padding: "8px 16px", fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "6px", background: '#A1C4E3', color: "white" }}
                      title="Download this member's stats as CSV"
                    >
                      Download CSV
                    </button>
                    <button className="btn-add-student" onClick={handleSendStatsEmail} disabled={emailSending} style={{ padding: "8px 16px", fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "6px", background: '#a1c4e3', color: "white" }} title="Send report to member email">
                      {emailSending ? <span className="mini-spinner"></span> : <FaEnvelope />} Send to Mail
                    </button>
                    <button className="btn-add-student" onClick={handleViewAttendanceReport} style={{ padding: "8px 16px", fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "6px" }} title="View detailed attendance report">
                      <FaFileAlt /> View Full Report
                    </button>
                  </div>
                </div>
              )}

              {statsLoading ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <div className="loading-spinner"></div>
                  <p style={{ marginTop: "15px", color: "#888" }}>Loading statistics...</p>
                </div>
              ) : statsData && statsData.isSpecialRule ? (
                // ========== SPECIAL INTERN UI ==========
                <div className="stats-modal-content">
                  <div style={{ background: "linear-gradient(135deg, #eef2ff 0%, #f0f4ff 100%)", borderRadius: "12px", padding: "20px", marginBottom: "12px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b", marginBottom: "12px" }}>
                      {statsEmployee?.employeeName}'s Intern Overview -{" "}
                      {monthNames[parseInt(statsMonthFilter.split("-")[1]) - 1]} {statsMonthFilter.split("-")[0]}
                    </h3>

                    <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#92400e', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <FaInfoCircle />
                      <span><strong>Special Intern Rules:</strong> Sunday off • Work window 12:00 PM – 5:00 PM • Weekly minimum 15h • Flexible days</span>
                    </div>

                    <div className="summary-cards">
                      <div className="summary-card">
                        <span className="summary-label">Total Expected Hours</span>
                        <span className="summary-value">{formatHoursMins(statsData.totalExpectedMonthMinutes)}</span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Actual Worked Hours</span>
                        <span className="summary-value" style={{ color: '#d97706' }}>
                          {formatHoursMins(statsData.totalWorkedMinutes)}
                        </span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Extra Hours</span>
                        <span className="summary-value" style={{ color: '#10b981' }}>
                          {formatHoursMins(statsData.extraMinutes)}
                        </span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Shortfall Hours</span>
                        <span className="summary-value" style={{ color: '#dc2626' }}>
                          {formatHoursMins(statsData.shortfallMinutes)}
                        </span>
                      </div>
                    </div>

                    <div className="summary-cards" style={{ marginTop: "12px" }}>
                      <div className="summary-card">
                        <span className="summary-label">Working Days / Total</span>
                        <span className="summary-value">
                          <span style={{ color: "#d97706" }}>{statsData.workingDaysPassed - statsData.totalAbsentDays}</span> / {statsData.totalWorkingDaysInMonth + (statsData.totalLeaveDaysInMonth || 0)}
                        </span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Absent Days</span>
                        <span className="summary-value" style={{ color: '#dc2626' }}>{statsData.totalAbsentDays}</span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Leave Days</span>
                        <span className="summary-value" style={{ color: '#8b5cf6' }}>{statsData.totalLeaveDaysInMonth || 0}</span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Weeks Tracked</span>
                        <span className="summary-value">{statsData.weeksInMonth}</span>
                      </div>
                    </div>
                  </div>

                  <div className="summary-card" style={{ marginTop: "12px", background: "linear-gradient(135deg, #f8fafc, #f1f5f9)", borderRadius: "12px", padding: "20px", border: "2px solid #e2e8f0" }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                      <span className="summary-label" style={{ color: '#475569', margin: 0, fontSize: '14px' }}>
                        📅 Weekly Breakdown (15h/week minimum)
                      </span>
                    </div>

                    {statsData.weeklySummary && statsData.weeklySummary.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {statsData.weeklySummary.map((week, i) => (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              background: week.meetsMinimum ? '#ecfdf5' : '#fef2f2',
                              border: `1px solid ${week.meetsMinimum ? '#86efac' : '#fecaca'}`,
                              gap: '12px',
                              flexWrap: 'wrap'
                            }}
                          >
                            <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '13px', minWidth: '130px' }}>
                              {week.weekLabel}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                              <div style={{ fontSize: '13px' }}>
                                <span style={{ color: '#64748b', fontWeight: '500' }}>Worked: </span>
                                <span style={{ fontWeight: '800', color: week.meetsMinimum ? '#059669' : '#dc2626', fontSize: '15px' }}>
                                  {formatHoursMins(week.workedMinutes)}
                                </span>
                                <span style={{ color: '#94a3b8', fontSize: '12px', marginLeft: '4px' }}>
                                  / {formatHoursMins(week.expectedMinutes)}
                                </span>
                              </div>

                              <div style={{ fontSize: '12px', color: '#64748b' }}>
                                <strong style={{ color: '#1e293b' }}>{week.daysWorked}</strong> day{week.daysWorked !== 1 ? 's' : ''} worked
                              </div>

                              {week.meetsMinimum ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', background: '#10b981', color: 'white', fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                  ✅ {week.extraMinutes > 0 ? `+${formatHoursMins(week.extraMinutes)} extra` : 'Meets 15h'}
                                </span>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', background: '#dc2626', color: 'white', fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                  ⚠️ Short {formatHoursMins(week.shortfallMinutes)}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '13px' }}>
                        No weeks tracked for this month
                      </div>
                    )}
                  </div>

                  <div className="summary-card" style={{ marginTop: "12px", background: "linear-gradient(135deg, #f8fafc, #f1f5f9)", borderRadius: "12px", padding: "20px", border: "2px solid #e2e8f0" }}>
                    <span className="summary-label" style={{ marginBottom: "12px", textAlign: 'center', color: '#475569', display: 'block' }}>
                      ⏱️ Time Breakdown
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      {(() => {
                        const absentTime = statsData.absentMinutes || 0;
                        const leaveTime = statsData.leaveMinutes || 0;
                        const shortfall = statsData.shortfallMinutes || 0;
                        const total = absentTime + leaveTime + shortfall;

                        return (
                          <>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#ef4444', fontFamily: 'monospace' }}>{formatHoursMins(absentTime)}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Absent</div>
                            </div>
                            <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>+</span>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#8b5cf6', fontFamily: 'monospace' }}>{formatHoursMins(leaveTime)}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Leave Days</div>
                            </div>
                            <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>+</span>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#f97316', fontFamily: 'monospace' }}>{formatHoursMins(shortfall)}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Weekly Shortfall</div>
                            </div>
                            <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>=</span>
                            <div style={{ textAlign: 'center', background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', borderRadius: '8px', padding: '6px 14px', border: '2px solid #e2e8f0' }}>
                              <div style={{ fontSize: '17px', fontWeight: '800', color: '#1e293b', fontFamily: 'monospace' }}>{formatHoursMins(total)}</div>
                              <div style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px', fontWeight: '600' }}>Total</div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {(absentDates.length > 0 || leaveDatesList.length > 0) && (
                    <div className="absent-dates-section" style={{ background: '#fef2f2', borderRadius: '12px', padding: '20px', border: '1px solid #fecaca', marginTop: '20px' }}>
                      <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#991b1b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <FaExclamationTriangle style={{ color: 'var(--danger)' }} />
                        Absent Dates ({absentDates.length} {absentDates.length === 1 ? 'day' : 'days'})
                        {leaveDatesList.length > 0 && (
                          <span style={{ marginLeft: '6px', color: '#059669', fontWeight: '600' }}>
                            + {leaveDatesList.length} leave {leaveDatesList.length === 1 ? 'day' : 'days'}
                          </span>
                        )}
                      </h4>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {absentDates.map((absentDay, index) => (
                          <span key={`absent-${index}`} style={{ background: '#dc2626', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <FaCalendarAlt style={{ opacity: 0.9, fontSize: '11px' }} />{absentDay.dayName} {absentDay.day}
                          </span>
                        ))}
                        {leaveDatesList.map((leaveDay, index) => (
                          <span key={`leave-${index}`} style={{ background: '#10b981', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <FaCalendarAlt style={{ opacity: 0.9, fontSize: '11px' }} />{leaveDay.dayName} {leaveDay.day}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : statsData ? (
                // ========== NORMAL EMPLOYEE UI ==========
                <div className="stats-modal-content">
                  <div className="monthly-summary" style={{ background: "linear-gradient(135deg, #eef2ff 0%, #f0f4ff 100%)", borderRadius: "12px", padding: "20px", marginBottom: "12px" }}>
                    <h3 className="summary-title" style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b", marginBottom: "16px" }}>
                      {statsEmployee?.employeeName}'s Monthly Overview -{" "}
                      {(() => {
                        const parts = statsMonthFilter.split("-");
                        if (parts.length === 2) return `${monthNames[parseInt(parts[1]) - 1]} ${parts[0]}`;
                        return statsMonthFilter;
                      })()}
                    </h3>
                    <div className="summary-cards">
                      <div className="summary-card">
                        <span className="summary-label">Working Days in Month</span>
                        <span className="summary-value">
                          {statsData.totalWorkingDaysInMonth + (statsData.totalLeaveDaysInMonth || 0)} days
                        </span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Total Expected Hours</span>
                        <span className="summary-value">{formatHoursMins(statsData.totalExpectedMonthMinutes)}</span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Leave Days</span>
                        <span className="summary-value" style={{ color: '#8b5cf6' }}>{statsData.totalLeaveDaysInMonth || 0} days</span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Daily Expected</span>
                        <span className="summary-value">{formatDuration(statsData.expectedDailyMinutes)}</span>
                      </div>
                    </div>
                    <div className="summary-cards" style={{ marginTop: "12px" }}>
                      <div className="summary-card">
                        <span className="summary-label">Days Worked / Total</span>
                        <span className="summary-value">
                          <span style={{ color: "#d97706" }}>{statsData.workingDaysPassed - statsData.totalAbsentDays}</span>{" "}
                          / {statsData.totalWorkingDaysInMonth + (statsData.totalLeaveDaysInMonth || 0)}
                        </span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Actual Hours / Expected</span>
                        <span className="summary-value">
                          <span style={{ color: "#d97706" }}>
                            {formatHoursMins(
                              applyExtraTime
                                ? statsData.totalWorkedMinutes
                                : Math.max(0, statsData.totalWorkedMinutes - (statsData.totalExtraTimeMinutes || 0))
                            )}
                          </span> / {formatHoursMins(statsData.totalExpectedMonthMinutes)}
                        </span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">⏸️ Total Office Outside Time</span>
                        <span className="summary-value" style={{ color: statsData.totalPauseMinutesMonth > 0 ? '#f59e0b' : '#1e293b' }}>
                          {formatHoursMins(statsData.totalPauseMinutesMonth)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="summary-card" style={{ marginTop: "12px", background: "linear-gradient(135deg, #f8fafc, #f1f5f9)", borderRadius: "12px", padding: "20px", border: "2px solid #e2e8f0" }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                      <span className="summary-label" style={{ color: '#475569', margin: 0 }}>⏱️ Time Breakdown</span>
                      {statsData.totalExtraTimeMinutes > 0 && (
                        <button
                          onClick={() => setApplyExtraTime((prev) => !prev)}
                          style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', fontWeight: '600', fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', background: applyExtraTime ? 'linear-gradient(135deg, #10b981, #059669)' : '#f1f5f9', color: applyExtraTime ? 'white' : '#475569', transition: 'all 0.2s ease' }}
                        >
                          {applyExtraTime ? `✓ Extra Applied (${formatHoursMins(statsData.totalExtraTimeMinutes)})` : `⚡ Apply Extra Time (${formatHoursMins(statsData.totalExtraTimeMinutes)})`}
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      {(() => {
                        const absentTime = (statsData.totalAbsentDays || 0) * statsData.expectedDailyMinutes;
                        const leaveDaysTime = (statsData.totalLeaveDaysInMonth || 0) * statsData.expectedDailyMinutes;
                        const lateTime = statsData.totalLateMinutes || 0;
                        const outsideTime = statsData.totalPauseMinutesMonth;
                        const earlyDepartureTime = statsData.totalEarlyDepartureMinutes || 0;
                        const extraTime = applyExtraTime ? (statsData.totalExtraTimeMinutes || 0) : 0;
                        const total = Math.max(0, absentTime + leaveDaysTime + lateTime + outsideTime + earlyDepartureTime - extraTime);

                        return (
                          <>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#ef4444', fontFamily: 'monospace' }}>{formatHoursMins(absentTime)}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Absent</div>
                            </div>
                            <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>+</span>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#8b5cf6', fontFamily: 'monospace' }}>{formatHoursMins(leaveDaysTime)}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Leave Days</div>
                            </div>
                            <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>+</span>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#f59e0b', fontFamily: 'monospace' }}>{formatHoursMins(lateTime)}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Late</div>
                            </div>
                            <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>+</span>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#f97316', fontFamily: 'monospace' }}>{formatHoursMins(earlyDepartureTime)}</div>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Early Dep.</div>
                            </div>
                            {applyExtraTime && (
                              <>
                                <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>-</span>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#10b981', fontFamily: 'monospace' }}>{formatHoursMins(extraTime)}</div>
                                  <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Extra</div>
                                </div>
                              </>
                            )}
                            <span style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '300' }}>=</span>
                            <div style={{ textAlign: 'center', background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', borderRadius: '8px', padding: '6px 14px', border: '2px solid #e2e8f0' }}>
                              <div style={{ fontSize: '17px', fontWeight: '800', color: '#1e293b', fontFamily: 'monospace' }}>{formatHoursMins(total)}</div>
                              <div style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px', fontWeight: '600' }}>Total</div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {(absentDates.length > 0 || leaveDatesList.length > 0) && (
                    <div className="absent-dates-section" style={{ background: '#fef2f2', borderRadius: '12px', padding: '20px', border: '1px solid #fecaca', marginTop: '20px' }}>
                      <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#991b1b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <FaExclamationTriangle style={{ color: 'var(--danger)' }} /> Absent Dates ({absentDates.length} {absentDates.length === 1 ? 'day' : 'days'})
                        {leaveDatesList.length > 0 && (
                          <span style={{ marginLeft: '6px', color: '#059669', fontWeight: '600' }}>
                            + {leaveDatesList.length} leave {leaveDatesList.length === 1 ? 'day' : 'days'}
                          </span>
                        )}
                      </h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {absentDates.map((absentDay, index) => (
                          <span key={`absent-${index}`} style={{ background: '#dc2626', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <FaCalendarAlt style={{ opacity: 0.9, fontSize: '11px' }} />{absentDay.dayName} {absentDay.day}
                          </span>
                        ))}
                        {leaveDatesList.map((leaveDay, index) => (
                          <span key={`leave-${index}`} style={{ background: '#10b981', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <FaCalendarAlt style={{ opacity: 0.9, fontSize: '11px' }} />{leaveDay.dayName} {leaveDay.day}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <div style={{ fontSize: "48px", marginBottom: "12px", color: 'var(--primary)' }}>
                    <FaClipboardList />
                  </div>
                  <p style={{ color: "#888", fontSize: "15px", fontWeight: "600" }}>No attendance data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ADD MODAL */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <div>
                <h3 className="modal-title">Add New Member</h3>
                <p className="modal-subtitle">Member will activate their account later</p>
              </div>
              <button className="modal-close" onClick={() => setShowAddModal(false)} aria-label="Close add member modal"><FaTimes /></button>
            </div>
            <div className="modal-body-custom">
              <form onSubmit={handleAddEmployee}>
                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label className="form-label">Full Name</label>
                    <input type="text" name="name" className="form-input" value={formData.name} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group form-group-half">
                    <label className="form-label">Email</label>
                    <input type="email" name="email" className="form-input" value={formData.email} onChange={handleInputChange} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label className="form-label">CNIC</label>
                    <input type="text" name="cnic" className="form-input" value={formData.cnic} onChange={handleInputChange} placeholder="12345-1234567-1" maxLength={15} required />
                  </div>
                  <div className="form-group form-group-half">
                    <label className="form-label">Employment Type</label>
                    <select name="employmentType" className="form-input" value={formData.employmentType} onChange={handleInputChange} required>
                      <option value="">Select type</option>
                      <option value="full_time">Full Time</option>
                      <option value="half_time">Half Time</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label className="form-label">Role</label>
                    <select name="role" className="form-input" value={formData.role} onChange={handleInputChange} required>
                      <option value="employee">Employee</option>
                      <option value="intern">Intern</option>
                      <option value="student">Student</option>
                    </select>
                  </div>
                  <div className="form-group form-group-half">
                    <label className="form-label">Field</label>
                    <select name="employeeField" className="form-input" value={formData.employeeField} onChange={handleInputChange}>
                      <option value="">Select field (optional)</option>
                      {employeeFields.map((field, index) => (
                        <option key={index} value={field}>{field}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label className="form-label">Expected Arrival</label>
                    <input type="time" name="expectedArrival" className="form-input" value={formData.expectedArrival} onChange={handleAddTimeChange} />
                  </div>
                  <div className="form-group form-group-half">
                    <label className="form-label">Expected Leaving</label>
                    <input type="time" name="expectedLeaving" className={`form-input ${addTimeError ? "input-error" : ""}`} value={formData.expectedLeaving} onChange={handleAddTimeChange} />
                    {addTimeError && <small className="text-danger" style={{ color: "#dc2626", fontSize: "11px", marginTop: "4px", display: "block" }}>{addTimeError}</small>}
                  </div>
                </div>
                <p className="text-muted small">The member will set their own password after activation.</p>
                <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                  <button type="button" className="btn-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
                  <button type="submit" className="btn-submit" disabled={isLoading} style={{ opacity: isLoading ? 0.6 : 1 }}>
                    {isLoading ? "Adding..." : "Add Member"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <div>
                <h3 className="modal-title">Edit Member</h3>
                <p className="modal-subtitle">Update details (password optional)</p>
              </div>
              <button className="modal-close" onClick={() => setShowEditModal(false)} aria-label="Close edit member modal"><FaTimes /></button>
            </div>
            <div className="modal-body-custom">
              <form onSubmit={handleUpdateEmployee}>
                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label className="form-label">Full Name</label>
                    <input type="text" name="name" className="form-input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
                  </div>
                  <div className="form-group form-group-half">
                    <label className="form-label">Email</label>
                    <input type="email" name="email" className="form-input" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label className="form-label">CNIC</label>
                    <input type="text" name="cnic" className="form-input" value={editForm.cnic} onChange={(e) => setEditForm({ ...editForm, cnic: e.target.value })} placeholder="12345-0000000-1" maxLength={15} required />
                  </div>
                  <div className="form-group form-group-half">
                    <label className="form-label">Employment Type</label>
                    <select name="employmentType" className="form-input" value={editForm.employmentType} onChange={(e) => setEditForm({ ...editForm, employmentType: e.target.value })} required>
                      <option value="">Select type</option>
                      <option value="full_time">Full Time</option>
                      <option value="half_time">Half Time</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label className="form-label">Role</label>
                    <select name="role" className="form-input" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} required>
                      <option value="employee">Employee</option>
                      <option value="intern">Intern</option>
                      <option value="student">Student</option>
                    </select>
                  </div>
                  <div className="form-group form-group-half">
                    <label className="form-label">Field</label>
                    <select name="employeeField" className="form-input" value={editForm.employeeField} onChange={(e) => setEditForm({ ...editForm, employeeField: e.target.value })}>
                      <option value="">Select field (optional)</option>
                      {employeeFields.map((field, index) => (
                        <option key={index} value={field}>{field}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label className="form-label">Expected Arrival</label>
                    <input type="time" name="expectedArrival" className="form-input" value={editForm.expectedArrival} onChange={handleEditTimeChange} />
                  </div>
                  <div className="form-group form-group-half">
                    <label className="form-label">Expected Leaving</label>
                    <input type="time" name="expectedLeaving" className={`form-input ${editTimeError ? "input-error" : ""}`} value={editForm.expectedLeaving} onChange={handleEditTimeChange} />
                    {editTimeError && <small className="text-danger" style={{ color: "#dc2626", fontSize: "11px", marginTop: "4px", display: "block" }}>{editTimeError}</small>}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">New Password (leave blank to keep current)</label>
                  <div className="password-input-wrapper">
                    <input type={showPassword ? "text" : "password"} name="password" className="form-input password-input" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Leave blank to keep current" />
                    <button type="button" className="password-toggle-btn" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                      {showPassword ? <FaEye /> : <FaEyeSlash />}
                    </button>
                  </div>
                </div>
                <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                  <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
                  <button type="submit" className="btn-submit" disabled={isLoading} style={{ opacity: isLoading ? 0.6 : 1 }}>
                    {isLoading ? "Updating..." : "Update Member"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;